import { authedFetch } from "./client";
import type { AccessConfig, AccessLevel } from "@bash/shared";

/** A standing grant shown on the permissions page. `label` is computed server-side (describeRule). */
export interface PermissionGrant {
  rule: string;
  source: "task" | "always";
  label: string;
}

/** The user's global default access settings. `accessConfig` non-null = the dial sits on Custom. */
export interface UserAccessSettings {
  accessLevel: AccessLevel;
  accessConfig: AccessConfig | null;
}

/**
 * Process-lived cache of the user's global default settings (level + custom
 * config), so the task-header picker can resolve an inherited level/Custom state
 * without a fetch per task view. Kept fresh by the setters; a stale read only
 * affects the "(default)"/Custom display label.
 */
let cachedSettings: UserAccessSettings | null = null;

/** Read the user's global default access settings (drives the settings card). */
export async function getUserAccessSettings(): Promise<UserAccessSettings> {
  const res = await authedFetch("/api/settings/access-level");
  const body = (await res.json()) as {
    accessLevel: AccessLevel;
    accessConfig?: AccessConfig | null;
  };
  cachedSettings = {
    accessLevel: body.accessLevel,
    accessConfig: body.accessConfig ?? null,
  };
  return cachedSettings;
}

/** Read the user's global default access level. */
export async function getUserAccessLevel(): Promise<AccessLevel> {
  return (await getUserAccessSettings()).accessLevel;
}

/** Read the user's global default settings, cached across the session (task-header picker). */
export async function getUserAccessSettingsCached(): Promise<UserAccessSettings> {
  return cachedSettings ?? getUserAccessSettings();
}

/** Read the user's global default level, cached across the session (task-header picker). */
export async function getUserAccessLevelCached(): Promise<AccessLevel> {
  return (await getUserAccessSettingsCached()).accessLevel;
}

/**
 * Set the user's global default access level. A preset write also clears any
 * stored custom config server-side (exactly-one-of PATCH semantics), so the
 * cached config clears with it.
 */
export async function setUserAccessLevel(
  accessLevel: AccessLevel
): Promise<AccessLevel> {
  const res = await authedFetch("/api/settings/access-level", {
    method: "PATCH",
    body: JSON.stringify({ accessLevel }),
  });
  const body = (await res.json()) as { accessLevel: AccessLevel };
  cachedSettings = { accessLevel: body.accessLevel, accessConfig: null };
  return body.accessLevel;
}

/** Set (or clear, with null) the user's custom access config. The level is untouched. */
export async function setUserAccessConfig(
  accessConfig: AccessConfig | null
): Promise<AccessConfig | null> {
  const res = await authedFetch("/api/settings/access-level", {
    method: "PATCH",
    body: JSON.stringify({ accessConfig }),
  });
  const body = (await res.json()) as { accessConfig?: AccessConfig | null };
  const config = body.accessConfig ?? null;
  if (cachedSettings)
    cachedSettings = { ...cachedSettings, accessConfig: config };
  return config;
}

/**
 * The standing grants. `complete` false: approvals made inside a task are not in the
 * list, because Bash was asleep (a plain read never wakes it) or did not answer.
 */
export interface PermissionGrantList {
  grants: PermissionGrant[];
  complete: boolean;
}

/**
 * List the standing grants for a task's sandbox ("Things Bash may always do").
 * A plain read never wakes Bash. `wake` is for the person asking ("Show them now").
 */
export async function listPermissionRules(
  taskId: string,
  opts: { wake?: boolean } = {}
): Promise<PermissionGrantList> {
  const res = await authedFetch(
    `/api/tasks/${taskId}/permission-rules${opts.wake ? "?wake=1" : ""}`
  );
  const body = (await res.json()) as {
    rules?: PermissionGrant[];
    complete?: boolean;
  };
  // A server without the field always woke Bash for the list: complete.
  return { grants: body.rules ?? [], complete: body.complete !== false };
}

/** Revoke one standing grant. Takes effect on the next matching tool call. */
export async function removePermissionRule(
  taskId: string,
  rule: string,
  source: "task" | "always"
): Promise<void> {
  await authedFetch(`/api/tasks/${taskId}/permission-rules`, {
    method: "DELETE",
    body: JSON.stringify({ rule, source }),
  });
}
